
import React, { FormEvent, useEffect, useState } from 'react';
import { Card, Form, Button, Col, Row } from "react-bootstrap";
import { v4 as uuidv4 } from 'uuid';
import { Link, useNavigate} from "react-router-dom";
import InvoiceItem, { Item } from './InvoiceItem';
import { useTranslation } from 'react-i18next';
import InputGroup from 'react-bootstrap/InputGroup';
import { confirmInvoiceNumberUsage, getNextInvoiceNumberPreview } from '../utils/invoiceHelper';
import { getInvoiceDate } from '../utils/dateSetter';
import { ValidationErrors } from '../validation/validationErrors';
import { generateRandomNumbr } from '../utils/randomNumbers';
import { validateForm } from '../validation/validationForm';
import { invoiceValidationSchema } from '../validation/invoice/invoiceValidationSchema';
import { Invoice } from '../types/invoiceForm';
import { Select } from './Select';
import { useFormState } from '../hooks/useStateHandler';
import { calculateSubTotal } from '../hooks/useDocumentForm';
import DocumentModal from './DocumentModal';
import ShowInvoiceDetails from './ShowInvoiceDetails';
import { GeneratePDF } from '../utils/documentHandler';
import { useBookInvoices } from '../hooks/useBookInvoices';
import { useInvoices } from '../hooks/useInvoices'
import { BookEntry } from '../types/bookInvoice';
import { formatToCroatianDate } from '../utils/dateHandler';



interface InvoiceFormProps {
  invoice?: Invoice; // Define 'invoice' type properly
  isEdit?: boolean;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ invoice, isEdit = false }) => {
  const { t } = useTranslation();
  const id = generateRandomNumbr();
  const uniqueId = invoice?.id? invoice.id : id;
  const navigate = useNavigate();
  const {addBookInvoice, clearBookInvoices, bookInvoices} = useBookInvoices();
  const {addInvoice, editInvoice} = useInvoices();

  // Initialize state 
  const { state, setState, handleChange, setField } = useFormState<Invoice>({
    id: uniqueId,
    iban: invoice?.iban || '',
    madeBy: invoice?.madeBy || '',
    currency: invoice?.currency || t('euro'),
    paymentMethod: 'bank',
    issueDate: invoice?.issueDate || new Date().toLocaleDateString(),
    invoiceNumber: invoice?.invoiceNumber || '1',
    dueDate: invoice?.dueDate || '',
    to: invoice?.to || '',
    toEmail: invoice?.toEmail || '',
    toAddress: invoice?.toAddress || '',
    from: invoice?.from || '',
    fromEmail: invoice?.fromEmail || '',
    fromAddress: invoice?.fromAddress || '',
    toOib: invoice?.toOib || '',
    fromOib: invoice?.fromOib || '',
    remark: invoice?.remark || '',
    total: invoice?.total || '0.00',
    subTotal: invoice?.subTotal || '0.00',
    taxRate: invoice?.taxRate || '0.00',
    taxAmmount: invoice?.taxAmmount || '0.00',
    discountRate: invoice?.discountRate || '0.00',
    discountAmmount: invoice?.discountAmmount || '0.00',
    items: invoice?.items || [
      {
        id: '1',
        name: '',
        description: '',
        price: '0.00',
        quantity: 1,
      }
    ]
  });
  
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationErrors<Invoice>>({});
  const [selectedpaymentMethod, setSelectedpaymentMethod] = useState<string>('bank');
  const paymentMethods = [
    {label: t('paymentMethod.bank'), value: 'bank'}, 
    {label: t('paymentMethod.card'), value: 'card'},
    {label: t('paymentMethod.cash'), value: 'cash'}
  ]
  
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      invoiceNumber: !isEdit? getNextInvoiceNumberPreview() : prevState.invoiceNumber,
      issueDate: getInvoiceDate(),
    }));
  }, [isEdit]);

  // Recalculate totals only when items change
  useEffect(() => {  
    handleCalculateTotal();
  }, [state.items, state.taxRate, state.discountRate]);  // watch only when needed
  

  const onItemizedItemEdit = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    evt.preventDefault();
    const name = evt.target.name;
    const itemId = evt.target.id;
    const value = evt.target.value;
    const newItems = state.items.map((item) =>
      item.id === itemId ? { ...item, [name]: value } : item
    ); 
    setField('items', newItems);
  };
  
  // Handle changes to any input field
  const editField = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    handleChange(event);    
  };

  // Handle adding a new item
  const handleAddEvent = (evt: FormEvent) => {
    evt.preventDefault();
    const id = uuidv4().substr(0, 3);
    const newItem = {
      id,
      name: '',
      price: '1.00',
      description: '',
      quantity: 1
    };
    setState(prevState => ({
      ...prevState,
      items: [...prevState.items, newItem]
    }));
  };

  // Handle deleting an item
  const handleRowDel = (i: Item) => {
    setState(prevState => ({
      ...prevState,
      items: prevState.items.filter(item => item.id !== i.id)
    }));
  };

  const onCurrencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCurrency = event.target.value;
    setField('currency', selectedCurrency);  
  };

  // Calculate the total, subtotal, tax, and discount
  const handleCalculateTotal = () => {
    const subTotal = calculateSubTotal(state.items); 
    setState(prevState => ({
      ...prevState,
      subTotal: subTotal.toFixed(2),
      taxAmmount: (subTotal * parseFloat(prevState.taxRate) / 100).toFixed(2),
      discountAmmount: (subTotal * parseFloat(prevState.discountRate) / 100).toFixed(2),
      total: (subTotal - parseFloat(prevState.discountAmmount) + parseFloat(prevState.taxAmmount)).toFixed(2)
    }));
  };

  // Open modal
  const openModal = (event: FormEvent) => {
    event.preventDefault();
    const validationErrors = validateForm(state, invoiceValidationSchema, t); // Validate the form
    setErrors(validationErrors); // Update the error state
    if (Object.keys(validationErrors).length === 0) {
      // No errors, submit the form
      setIsOpen(true);
    }
  };

const handleOnSave = () => {
    if(!isEdit)confirmInvoiceNumberUsage();
    // const { closeModal, ...newInvoice } = invoice;
    // const invoiceData = {id:newInvoice.info.invoiceNumber,...newInvoice}
    if(window.location.pathname.includes('edit')){
      editInvoice(state)
    }else{
      addInvoice(state);
      const entry: BookEntry = {
        id: bookInvoices[0] ? bookInvoices[0].entries[bookInvoices[0].entries.length - 1].id + 1 : 1,
        date: formatToCroatianDate(state.issueDate)  ,
        receiptNumber: state.invoiceNumber,
        description: t('invoice'),
        cashAmount: '0.00',
        cashlessAmount: parseFloat(state.total).toFixed(2),
        totalAmount: parseFloat(state.total).toFixed(2)
      }
      console.log(entry)
      const meta = bookInvoices[0]?.meta || {
        id: 0,
        activityName: '',
        activityCode: '',
        entrepreneurName: '',
        address: '',
        oib: '',
        businessUnitName: '',
        businessUnitAddress: ''
      };

      const bookData = {
        meta: meta,
        entries: [...(bookInvoices[0]?.entries || []), entry],
      };
      clearBookInvoices();
      addBookInvoice(bookData);
      // return navigate('/invoice-book')
    }
    navigate("/invoices")
  }

  const handleOnDownload = () => {
    GeneratePDF(state.invoiceNumber);
  }

  // Close modal
  const closeModal = () => setIsOpen(false);

  return (
    <>
      <Form onSubmit={openModal}>
        <Row>
          <Col md={8} lg={9}>
            <Card className="p-4 p-xl-5 my-3 my-xl-4">
              <div className="d-flex flex-row align-items-start justify-content-between mb-3">
                <div className="d-flex flex-column">
                  <div className="d-flex flex-column">
                    <div className="mb-2">
                      <span className="fw-bold">{t('issueDate')}:&nbsp;</span>
                      <span className="current-date">{state?.issueDate}</span>
                    </div>
                  </div>
                  <div className="d-flex flex-row align-items-center">
                    <span className="fw-bold d-block me-2">{t('dueDate')}:&nbsp;</span>
                    <Form.Control type="date" value={state.dueDate} name={"dueDate"} onChange={editField} style={{ maxWidth: '150px' }} required />
                  </div>
                </div>
                <div className="d-flex flex-row align-items-center">
                  <span className="fw-bold me-2">{t('invoiceNumber')}:&nbsp;{state.invoiceNumber}</span>
                </div>
              </div>
              <hr className="my-4" />
              <Row>
                <Col>
                  <Form.Label className="fw-bold">{t('to')}:</Form.Label>
                  <Form.Control placeholder={t('placeholder.to')} rows={3} as="textarea" value={state.to} type="text" name="to" className="my-2" onChange={editField} autoComplete="name" required />
                  <Form.Control placeholder={t('placeholder.toEmail')} value={state.toEmail} type="email" name="toEmail" className="my-2" onChange={editField} autoComplete="email" required />
                  <Form.Control placeholder={t('placeholder.toAddress')} value={state.toAddress} type="text" name="toAddress" className="my-2" autoComplete="address" onChange={editField} required />
                  <Form.Control placeholder={t('placeholder.toOib')} value={state.toOib} type="text" name="toOib" className="my-2" autoComplete="name" onChange={editField} required />
                  {errors.toOib && (
                     <span className="text-sm" style={{color: 'red'}}>{errors.toOib}</span>
                  )}

                </Col>
                <Col>
                  <Form.Label className="fw-bold">{t('from')}:</Form.Label>
                  <Form.Control placeholder={t('placeholder.from')} rows={3} as="textarea" value={state.from} type="text" name="from" className="my-2" onChange={editField} autoComplete="name" required />
                  <Form.Control placeholder={t('placeholder.fromEmail')} value={state.fromEmail} type="email" name="fromEmail" className="my-2" onChange={editField} autoComplete="email" required />
                  <Form.Control placeholder={t('placeholder.fromAddress')} value={state.fromAddress} type="text" name="fromAddress" className="my-2" autoComplete="address" onChange={editField} required />
                  <Form.Control placeholder={t('placeholder.fromOib')} value={state.fromOib} type="text" name="fromOib" className="my-2" autoComplete="name" onChange={editField} required />
                  {errors.fromOib && (
                     <span className="text-sm" style={{color: 'red'}}>{errors.fromOib}</span>
                  )}
                </Col>
              </Row>
              <Row  className="mb-5">
                <Col>
                  {selectedpaymentMethod === 'bank' && (
                    <Form.Control placeholder={t('placeholder.iban')} value={state.iban} type="text" name="iban" className="my-2" autoComplete="name" onChange={editField} required />
                  )}
                  {errors.iban && selectedpaymentMethod === 'bank' &&   (
                      <span className="text-sm" style={{color: 'red'}}>{errors.iban}</span>
                  )}
                  
                    <Form.Control placeholder={t('placeholder.madeBy')} value={state.madeBy} type="text" name="madeBy" className="my-2" autoComplete="name" onChange={editField} />
                    <Select
                      label={t('')}
                      options={paymentMethods}
                      value={selectedpaymentMethod}
                      placeholder={t('paymentMethod.select')}
                      onChange={(val) => {
                        setSelectedpaymentMethod(val);  
                        if(val !== 'bank'){
                          setField('iban', ''); // Clear IBAN if not bank
                          setField('remark', '')
                        }
                      }}
                      id="document-type"
                    />
                  </Col>
              </Row>
              <InvoiceItem
                onItemizedItemEdit={onItemizedItemEdit}
                onRowAdd={handleAddEvent}
                onRowDel={handleRowDel}
                currency={state.currency}
                items={state.items}
              />
              <Row className="mt-4 justify-content-end">
                <Col lg={6}>
                  <div className="d-flex flex-row align-items-start justify-content-between">
                    <span className="fw-bold">{t('subTotal')}:</span>
                    <span>{state.currency}{state.subTotal}</span>
                  </div>
                  <div className="d-flex flex-row align-items-start justify-content-between mt-2">
                    <span className="fw-bold">{t('discount')}:</span>
                    <span>
                      <span className="small">({state.discountRate || 0}%)</span>
                      {state.currency}{state.discountAmmount || 0}</span>
                  </div>
                  <div className="d-flex flex-row align-items-start justify-content-between mt-2">
                    <span className="fw-bold">{t('tax')}:</span>
                    <span>
                      <span className="small">({state.taxRate || 0}%)</span>
                      {state.currency}{state.taxAmmount || 0}</span>
                  </div>
                  <hr />
                  <div className="d-flex flex-row align-items-start justify-content-between" style={{ fontSize: '1.125rem' }}>
                    <span className="fw-bold">{t('total')}:</span>
                    <span className="fw-bold">{state.currency}{state.total || 0}</span>
                  </div>
                </Col>
              </Row>
              <hr className="my-4" />
              <Form.Label className="fw-bold">{t('remark')}:</Form.Label>
              <Form.Control placeholder={t('placeholder.remark')} name="remark" value={state.remark} onChange={editField} as="textarea" className="my-2" rows={1} />
            </Card>
          </Col>
          <Col md={4} lg={3}>
            <div className="sticky-top pt-md-3 pt-xl-4">
              <Button variant="primary" type="submit" className="d-block w-100 mb-2">{t('reviewInvoice')}</Button>
              <Link to="/">
                <Button variant="danger" className="d-block w-100">{t('cancelAndBackHome')}</Button>
              </Link>

              <DocumentModal showModal={isOpen} closeModal={closeModal} onDownload={handleOnDownload } onSave={handleOnSave} childProps={state}>
                  {(childProps) => (
                    <ShowInvoiceDetails invoice={childProps} />
                  )}
              </DocumentModal>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('currency')}:</Form.Label>
                <Form.Select value={state.currency} onChange={onCurrencyChange} className="btn btn-light my-1" aria-label="Change Currency">
                  <option value="€">EUR (Euro)</option>
                  <option value="$">USD (United States Dollar)</option>
                  <option value="£">GBP (Pound Sterling)</option>
                  <option value="€">EUR (Euro)</option>                                        
                  <option value="¥">JPY (Japanese Yen)</option>
                  <option value="$">CAD (Canadian Dollar)</option>
                  <option value="$">AUD (Australian Dollar)</option>
                  <option value="$">SGD (Signapore Dollar)</option>
                  <option value="¥">CNY (Chinese Renminbi)</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="my-3">
                  <Form.Label className="fw-bold">{t('tax')}:</Form.Label>
                  <InputGroup className="my-1 flex-nowrap">
                    <Form.Control name="taxRate" type="number" value={state.taxRate} onChange={(event) => editField(event)} className="bg-white border" placeholder="0.0" min="0.00" step="0.01" max="100.00" />
                    <InputGroup.Text className="bg-light fw-bold text-secondary small">%</InputGroup.Text>                    
                  </InputGroup>
                  {errors.taxRate && (
                        <span className="text-sm" style={{color: 'red'}}>{errors.taxRate}</span>
                    )}
              </Form.Group>
              <Form.Group className="my-3">
                <Form.Label className="fw-bold">{t('discount')}:</Form.Label>
                <InputGroup className="my-1 flex-nowrap">
                  <Form.Control name="discountRate" type="number" value={state.discountRate} onChange={(event) => editField(event)} className="bg-white border" placeholder="0.0" min="0.00" step="0.01" max="100.00" />
                  <InputGroup.Text className="bg-light fw-bold text-secondary small">%</InputGroup.Text>                  
                </InputGroup>
                {errors.discountRate && (
                        <span className="text-sm" style={{color: 'red'}}>{errors.discountRate }</span>
                    )}
              </Form.Group>              
            </div>
          </Col>
        </Row>
      </Form>
      
    </>
  );
};

export default InvoiceForm;

